import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterSection = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribed(true);
        setEmail("");
        toast.success(data.message);
      } else {
        toast.error(data.detail || "Erreur lors de l'inscription");
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      toast.error("Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 md:py-20" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative p-8 md:p-12 rounded-3xl bg-gradient-to-br from-primary/10 via-background to-cyan-500/10 border border-primary/20 overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

          <div className="relative text-center">
            {subscribed ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-4"
              >
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                  {t("newsletter.success")}
                </h3>
                <p className="text-muted-foreground">
                  {t("newsletter.subtitle")}
                </p>
              </motion.div>
            ) : (
              <>
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Mail className="h-7 w-7 text-primary" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-3">
                  {t("newsletter.title")}
                </h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  {t("newsletter.subtitle")}
                </p>

                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                >
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("newsletter.placeholder")}
                    required
                    className="flex-1 rounded-full px-5"
                    data-testid="newsletter-email"
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="rounded-full px-6 gap-2"
                    data-testid="newsletter-submit"
                  >
                    {loading ? (
                      "..."
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        {t("newsletter.subscribe")}
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default NewsletterSection;
